// Three-step "duplicate object" modal launched from the test-sites
// table (ClientDetail). Walks the user through:
//   1. Info: what gets copied / not copied.
//   2. Pick which of the source object's inspections to also clone.
//   3. Pick N (1–10). Continue → run the cloning loop with a
//      full-screen LoadingScreen freeze.
//
// None of the modals can be dismissed by clicking outside; only the
// Cancel / Continue / ✕ buttons close them.

import React, { useEffect, useState } from 'react';
import ModalTemplate from './templates/ModalTemplate';
import LoadingScreen from './LoadingScreen';
import {
  getObjectById, getInspectionsByObject, getInspectionById,
} from '../utils/storage';
import { post, put } from '../api';
import './DuplicateObjectModal.css';

// Fields that the InspectionWizard "Edit" modal manages — the user
// explicitly requested that ONLY these be copied when duplicating an
// inspection. Anything outside this list (synopsis text, captures
// metadata, ut_data, results, …) is left at its default. The
// 3D-Inspect URL is intentionally absent, and so is the assignee
// (assignedReporter) — copies start unassigned.
const INSPECTION_INFO_FIELDS = [
  'specificCode', 'terraProcedure', 'acceptanceStandard',
  'subject',
  'technique', 'typeOfInspection', 'extentOfTesting',
  'surfaceCondition', 'surfaceTemp', 'insideOutside', 'manualOrDrone',
  'couplant',
  'equipment', 'equipmentModules', 'probes', 'stepwedges',
  'ndtOperators', 'ndtReporter',
];

function pickInspectionInfo(formData) {
  if (!formData || typeof formData !== 'object') return {};
  const out = {};
  for (const key of INSPECTION_INFO_FIELDS) {
    if (key in formData) out[key] = formData[key];
  }
  return out;
}

// Fields on the source Object that are intentionally excluded from
// the copy per the user's spec. Mirrors `_EXCLUDED_OBJECT_FIELDS` in
// any future backend duplicate endpoint.
function pickObjectCopyPayload(srcObj) {
  return {
    design_spec:           srcObj.designSpec ?? null,
    general_info:          srcObj.generalInfo ?? null,
    settings:              srcObj.settings ?? null,
    captures_object_type:  srcObj.capturesObjectType || '',
    captures_composition:  srcObj.capturesComposition || '',
    // SKIP: google_drive_path, three_d_inspect_url, object_photo
  };
}

export default function DuplicateObjectModal({
  clientId, object, onClose, onDone,
}) {
  const [step, setStep] = useState(1);              // 1 | 2 | 3
  const [insps, setInsps] = useState([]);
  const [inspsLoaded, setInspsLoaded] = useState(false);
  const [selectedInspIds, setSelectedInspIds] = useState(() => new Set());
  const [copyCount, setCopyCount] = useState(1);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState('');

  // Step 2 needs the source object's inspections so the user can pick
  // which to clone. Lazy-load on entry to step 2 so step 1 opens
  // immediately.
  useEffect(() => {
    if (step !== 2 || inspsLoaded) return;
    let cancelled = false;
    getInspectionsByObject(object.id)
      .then((rows) => { if (!cancelled) { setInsps(rows || []); setInspsLoaded(true); } })
      .catch(() => { if (!cancelled) { setInsps([]); setInspsLoaded(true); } });
    return () => { cancelled = true; };
  }, [step, inspsLoaded, object.id]);

  const toggleInsp = (id) => {
    setSelectedInspIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  async function runDuplicate() {
    setCopying(true);
    setError('');
    try {
      // Refetch the source object once with the latest design / general
      // info so an in-flight edit by another tab can't race the copy.
      const src = await getObjectById(object.id);
      if (!src) throw new Error('Source object disappeared');

      // Inspections to clone: refetch each by id (full row) so we get
      // the latest form_data.
      const selectedInspRows = [];
      for (const id of selectedInspIds) {
        try {
          const full = await getInspectionById(id);
          if (full) selectedInspRows.push(full);
        } catch {
          // ignore — partial clone is better than failing the whole run
        }
      }

      const objPayload = pickObjectCopyPayload(src);

      for (let n = 1; n <= copyCount; n++) {
        const newTag = `${src.objectTag || ''} (${n})`.trim();

        // 1. Create the bare object with type + tag.
        const created = await post('/objects', {
          client_id: clientId,
          type: src.type || '',
          object_tag: newTag,
        });
        if (!created?.id) throw new Error('Failed to create duplicate object');

        // 2. Patch in the copied fields (design, general info, etc.) —
        //    google_drive_path / three_d_inspect_url / photo stay empty.
        if (Object.keys(objPayload).length > 0) {
          await put(`/objects/${created.id}`, objPayload);
        }

        // 3. Clone each chosen inspection. Only the wizard "Inspection
        //    Information" fields are copied — measurements / results /
        //    captures are left empty as if newly created.
        for (const insp of selectedInspRows) {
          const newInsp = await post('/inspections', {
            object_id: created.id,
            name: insp.name || '',
            description: insp.description || '',
            type: insp.type || '',
            status: 'Planning',
          });
          if (!newInsp?.id) continue;
          const formData = pickInspectionInfo(insp.formData || {});
          if (Object.keys(formData).length > 0) {
            await put(`/inspections/${newInsp.id}`, { form_data: formData });
          }
        }
      }
    } catch (e) {
      console.error('Duplicate failed:', e);
      setError(e?.message || 'Duplicate failed');
      setCopying(false);
      return;
    }

    setCopying(false);
    onDone?.();
  }

  // Render: during the copy phase we hide the modal entirely and show
  // a full-screen LoadingScreen freeze so the user can't fire other
  // actions mid-run.
  if (copying) {
    return <LoadingScreen isLoading={true} onDone={() => {}} />;
  }

  // ── Step 1 — intro / what gets copied ────────────────────────
  if (step === 1) {
    return (
      <ModalTemplate
        title="Duplicate object"
        onClose={onClose}
        closeOnBackdrop={false}
        actions={
          <>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => setStep(2)}>Continue</button>
          </>
        }
      >
        <p className="dom-hint">
          You're about to duplicate <strong>{object.objectTag || '—'}</strong>.
          The duplicate is not created until you finish all the steps.
        </p>
        <div className="dom-section-label">What gets copied</div>
        <ul className="dom-bullets">
          <li>All object information (Design, General info, Captures settings)</li>
          <li>All assessment information</li>
          <li>Labels left empty stay empty</li>
        </ul>
        <div className="dom-section-label">What does not get copied</div>
        <ul className="dom-bullets">
          <li>Google Drive path</li>
          <li>3D Inspect URL</li>
          <li>Object photo (in Assessment)</li>
        </ul>
      </ModalTemplate>
    );
  }

  // ── Step 2 — pick inspections to clone ───────────────────────
  if (step === 2) {
    const allSelected =
      insps.length > 0 && insps.every((i) => selectedInspIds.has(i.id));
    return (
      <ModalTemplate
        title="Duplicate inspections of this object"
        onClose={onClose}
        closeOnBackdrop={false}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>Continue</button>
          </>
        }
      >
        <p className="dom-hint">
          Tick the inspections to clone alongside the new object. Only the
          Inspection Information form fields are copied — measurements,
          results, and captures stay empty as if this were a new inspection.
          3D Inspect URL is not copied.
        </p>
        {!inspsLoaded ? (
          <p className="dom-insp-empty">Loading inspections…</p>
        ) : insps.length === 0 ? (
          <p className="dom-insp-empty">
            This object has no inspections to duplicate.
          </p>
        ) : (
          <>
            <label className="dom-insp-select-all">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedInspIds(new Set(insps.map((i) => i.id)));
                  } else {
                    setSelectedInspIds(new Set());
                  }
                }}
              />
              Select all
            </label>
            <div className="dom-insp-list">
              {insps.map((insp) => {
                const active = selectedInspIds.has(insp.id);
                return (
                  <label
                    key={insp.id}
                    className={`dom-insp-item${active ? ' dom-insp-item-active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleInsp(insp.id)}
                    />
                    <span className="dom-insp-code">{insp.type || '—'}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </ModalTemplate>
    );
  }

  // ── Step 3 — number of copies ────────────────────────────────
  return (
    <ModalTemplate
      title="How many copies?"
      onClose={onClose}
      closeOnBackdrop={false}
      actions={
        <>
          <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
          <button className="btn btn-primary" onClick={runDuplicate}>
            Continue
          </button>
        </>
      }
    >
      <p className="dom-hint">
        How many duplicates would you like to create?
      </p>
      <label className="dom-count-row">
        <span className="dom-section-label" style={{ margin: 0 }}>
          Number of copies
        </span>
        <select
          className="dom-count-select"
          value={copyCount}
          onChange={(e) => setCopyCount(parseInt(e.target.value, 10) || 1)}
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </label>
      {error && <p className="dom-error">{error}</p>}
    </ModalTemplate>
  );
}
